class PageHeaderSection extends HTMLElement {
  constructor() {
    super();

    this.isSticky = this.dataset.sticky === "true";
    this.isStickyAlways = this.dataset.stickyAlways === "true";
    this.isTransparent = this.dataset.transparent === "true";
    this.isAlwaysMobileMenu = this.dataset.alwaysMobileMenu === "true";

    // Optional CSS selector for the section over which the header should hide
    this.hideOverSelector = this.dataset.hideOverSelector || null;

    this.classBodyAlwaysMobileMenu = "mobile-nav";

    this.header = document.querySelector(".page-header");
    this.desktopMenuTrigger = document.querySelector(
      ".wt-header__sticky-menu-trigger",
    );
    this.desktopMenuBar = document.querySelector(".wt-drawer--nav");

    this.enabledClass = "sticky-enabled";
    this.showClass = "sticky-show";
    this.desktopHeaderWithMenuBarClass = "page-header--sticky-show-menubar-lg";
  }

  connectedCallback() {
    this._onSignedOutTextClick = () => {
      document.querySelector(".signed-out-avatar-menu")?.click();
    };
    document.querySelector(".signed-out-text")?.addEventListener("click", this._onSignedOutTextClick);

    if (window.Shopify?.designMode) {
      this._onSectionLoad = () => this.captureSearchGutter();
      document.addEventListener("shopify:section:load", this._onSectionLoad);
    }

    this.init();
  }

  disconnectedCallback() {
    this.disableStickyHeader();
    document.querySelector(".signed-out-text")?.removeEventListener("click", this._onSignedOutTextClick);
    if (this._onSectionLoad) {
      document.removeEventListener("shopify:section:load", this._onSectionLoad);
      this._onSectionLoad = null;
    }
  }

  getHeaderHeight() {
    return this.header ? this.header.offsetHeight : 0;
  }

  /** Returns the target element for the hide-over behavior (if any). */
  getHideOverTarget() {
    if (!this.hideOverSelector) return null;
    if (this._hideOverTarget && document.body.contains(this._hideOverTarget)) {
      return this._hideOverTarget;
    }
    this._hideOverTarget =
      document.querySelector(this.hideOverSelector) || null;
    return this._hideOverTarget;
  }

  /**
   * Should the sticky header be hidden because its bottom edge reached
   * the target section's top edge?
   *
   * Geometry reference (viewport coordinates):
   * - header top: usually 0 when sticky is shown
   * - header bottom: header.offsetHeight
   * - section top: target.getBoundingClientRect().top
   *
   * We hide when sectionTop <= headerBottom.
   */
  shouldHideOverSection() {
    const target = this.getHideOverTarget();
    if (!target || !this.header) return false;

    const rect = target.getBoundingClientRect();
    const headerH = this.getHeaderHeight();

    // Small hysteresis to avoid flicker on exact boundary (in px)
    const EPS = 1;

    // True when the section intersects with the header band [0, headerH)
    // i.e. sectionTop < headerBottom && sectionBottom > 0
    const overlapsHeaderBand = rect.top < headerH - EPS && rect.bottom > EPS;

    return overlapsHeaderBand;
  }

  /** Attach / detach a lightweight resize handler to keep things in sync. */
  attachResizeHandler() {
    // Re-run calculations on resize; cheap logic, no throttling needed here
    this._onResize = () => {
      // Re-capture the natural header height for option_1 mobile (no-op
      // when sticky is already engaged so we don't grab the compact height).
      this.setNaturalHeaderHeight();
      // Force potential reflow-based checks in next scroll tick
      // so currentScrollPos logic stays consistent.
      this.scrollHandler?.();
    };
    window.addEventListener("resize", this._onResize, { passive: true });
  }

  detachResizeHandler() {
    if (this._onResize) {
      window.removeEventListener("resize", this._onResize);
      this._onResize = null;
    }
  }

  /**
   * Capture the header's natural (non-sticky) offsetHeight into the
   * --header-natural-height CSS variable on documentElement. Used by
   * option_1 mobile to pad <main> when the header switches to
   * position: fixed once sticky engages — the padding is the constant
   * natural height, so content doesn't reflow when compact mode collapses
   * the header inside the fixed area.
   *
   * Only writes when .sticky-enabled is NOT on the header, otherwise we'd
   * capture the smaller compact height and content would shift.
   */
  setNaturalHeaderHeight() {
    if (!this.header) return;
    if (this.header.classList.contains(this.enabledClass)) return;
    document.documentElement.style.setProperty(
      "--header-natural-height",
      `${this.header.offsetHeight}px`,
    );

    this.captureSearchGutter();
  }


  captureSearchGutter() {
    if (!this.classList.contains("wt-header--v1")) return;
    if (!window.matchMedia("(min-width: 1200px)").matches) return;

    const headerSearchEl = document.querySelector(".wt-header__search");
    if (!headerSearchEl) return;

    document.documentElement.style.removeProperty("--header-search-margin-right");

    requestAnimationFrame(() => {
      const marginRight = window.getComputedStyle(headerSearchEl).marginRight;
      if (marginRight && marginRight !== "0px") {
        document.documentElement.style.setProperty(
          "--header-search-margin-right",
          marginRight,
        );
      }
    });
  }

  enableStickyHeader() {
    if (!this.header) {
      console.error("Header element not found for enabling sticky header");
      return;
    }

    document.body.classList.add("page-header-sticky");

    let prevScrollpos = window.pageYOffset;

    const isDesktop = window.matchMedia("(min-width: 1200px)").matches;
    const isMenuBarOpen = () =>
      this.header.classList.contains(this.desktopHeaderWithMenuBarClass);
    const isHeaderWithDesktopNav =
      !document.body.classList.contains("mobile-nav");
    const allLLevelsLinks =
      this.desktopMenuBar?.querySelectorAll("[data-menu-level]");
    const onlyLevel1Links = this.desktopMenuBar?.querySelectorAll(
      "[data-menu-level='1']",
    );

    const header = document.querySelector("#header");

    const calculateNavbarTopMargin = () => {
      const navbar = document.querySelector("#wt-drawer-nav");
      if (!navbar || !header) return 0;

      let marginTop = 0;

      if (navbar.offsetHeight > header.offsetHeight) {
        marginTop = header.offsetHeight - navbar.offsetHeight;
      } else {
        marginTop = Math.abs(navbar.offsetHeight - header.offsetHeight);
      }

      navbar.style.setProperty("--top-margin", `${marginTop}px`);

      return marginTop;
    };

    const calculateStickyFiltersTopOffset = (value = 0) => {
      const stickyFilters = document.querySelector(
        ".collection__sticky-header",
      );
      const plpWrapper = document.querySelector(".collection-grid-section");

      if (stickyFilters && plpWrapper) {
        const offset = value ?? `${this.header.offsetHeight}px`;
        plpWrapper.style.setProperty("--filters-sticky-offset", offset);
      }
    };

    calculateNavbarTopMargin();
    calculateStickyFiltersTopOffset();
    this.setNaturalHeaderHeight();

    const stickyHeader = {
      show: () => {
        if (this.header) this.header.classList.add(this.showClass);
        stickyHeader.visible = true;
        stickyHeader.handleBehavior();
        calculateNavbarTopMargin();
        calculateStickyFiltersTopOffset();
      },
      hide: () => {
        if (this.header) this.header.classList.remove(this.showClass);
        stickyHeader.visible = false;
        stickyHeader.handleBehavior();
        calculateStickyFiltersTopOffset(0);
      },
      enable: () => {
        if (this.header) this.header.classList.add(this.enabledClass);
        stickyHeader.enabled = true;
        stickyHeader.handleBehavior();
      },
      disable: () => {
        if (this.header)
          this.header.classList.remove(this.enabledClass, this.showClass);
        stickyHeader.enabled = false;
        stickyHeader.handleBehavior();
      },
      enabled: false,
      visible: true,
      handleBehavior: () => {
        if (isHeaderWithDesktopNav && isDesktop && this.header) {
          stickyHeader.log();
          if (!isMenuBarOpen() && stickyHeader.enabled) {
            setTabindex(allLLevelsLinks, "-1");
          }
          if (isMenuBarOpen() && stickyHeader.enabled) {
            setTabindex(allLLevelsLinks, "0");
          }
          if (!stickyHeader.enabled) {
            setTabindex(onlyLevel1Links, "0");
            if (this.desktopMenuTrigger)
              setTabindex([this.desktopMenuTrigger], "-1");
          } else if (this.desktopMenuTrigger)
            setTabindex([this.desktopMenuTrigger], "0");
        }
      },
      log: () => {},
    };

    // --- Scroll handler with "hide-over-section" override ----------
    this.scrollHandler = () => {
      const currentScrollPos = window.pageYOffset;

      // If we're over the target section, force-hide regardless of isStickyAlways.
      if (this.shouldHideOverSection()) {
        stickyHeader.hide();
      } else {
        // Original behavior preserved
        if (!this.isStickyAlways) {
          if (prevScrollpos > currentScrollPos) {
            stickyHeader.show();
          } else {
            stickyHeader.hide();
          }
        } else {
          stickyHeader.show();
        }
      }

      prevScrollpos = currentScrollPos;
    };

    window.addEventListener("scroll", this.scrollHandler, { passive: true });
    this.attachResizeHandler();

    this.desktopMenuTrigger?.addEventListener("click", (e) => {
      e.preventDefault();
      this.desktopMenuBar?.classList.toggle("wt-drawer--nav-show");
      this.desktopMenuTrigger?.classList.toggle(
        "wt-header__sticky-menu-trigger--active",
      );
      this.header?.classList.toggle(this.desktopHeaderWithMenuBarClass);
      stickyHeader.handleBehavior();
    });

    const sentinel = document.querySelector(".sticky-header__threshold");
    const handleStickySentinel = (entries) => {
      entries.forEach(({ isIntersecting }) => {
        if (isIntersecting) {
          stickyHeader.disable();
        } else {
          stickyHeader.enable();
        }
      });
    };

    this.stickyHeaderObserver = new IntersectionObserver(handleStickySentinel, {
      root: null,
      rootMargin: `${this.isStickyAlways ? "-160" : "-100"}px 0px 0px 0px`,
      threshold: 0,
    });
    if (sentinel) this.stickyHeaderObserver.observe(sentinel);
  }

  disableStickyHeader() {
    if (this.header) {
      // Remove classes added by sticky header
      this.header.classList.remove(
        this.enabledClass,
        this.showClass,
        this.desktopHeaderWithMenuBarClass,
      );
      document.body.classList.remove("page-header-sticky");
      this.desktopMenuBar?.classList.remove("wt-drawer--nav-show");
      this.desktopMenuTrigger?.classList.remove(
        "wt-header__sticky-menu-trigger--active",
      );
    }

    // Remove the scroll event listener
    if (this.scrollHandler) {
      window.removeEventListener("scroll", this.scrollHandler);
      this.scrollHandler = null;
    }

    // Detach resize handler
    this.detachResizeHandler();

    // Disconnect the IntersectionObserver
    if (this.stickyHeaderObserver) {
      this.stickyHeaderObserver.disconnect();
      this.stickyHeaderObserver = null;
    }
  }

  initDropdowns() {
    const teaserMenu = document.querySelector(".wt-header__nav-teaser__list");
    if (!teaserMenu) return;

    // Prevent duplicate initialization
    if (this._dropdownsInitialized) return;
    this._dropdownsInitialized = true;

    const parentItems = teaserMenu.querySelectorAll(
      ".wt-header__nav-teaser__item--parent"
    );

    // Use event delegation for click outside
    const handleClickOutside = (e) => {
      const isMobile = window.matchMedia("(max-width: 1199px)").matches;
      if (!isMobile) return;

      parentItems.forEach((item) => {
        if (!item.contains(e.target)) {
          item.setAttribute("aria-expanded", "false");
          const link = item.querySelector(".wt-header__nav-teaser__link--parent");
          if (link) {
            link.setAttribute("aria-expanded", "false");
          }
        }
      });
    };

    document.addEventListener("click", handleClickOutside);

    parentItems.forEach((item) => {
      const link = item.querySelector(".wt-header__nav-teaser__link--parent");
      const dropdown = item.querySelector(".wt-header__nav-teaser__dropdown");

      if (!link || !dropdown) return;

      // Handle click events (mobile)
      const handleClick = (e) => {
        const isMobile = window.matchMedia("(max-width: 1199px)").matches;
        if (!isMobile) return;

        e.preventDefault();
        const isExpanded = item.getAttribute("aria-expanded") === "true";

        // Close all other dropdowns
        parentItems.forEach((otherItem) => {
          if (otherItem !== item) {
            otherItem.setAttribute("aria-expanded", "false");
            const otherLink = otherItem.querySelector(
              ".wt-header__nav-teaser__link--parent"
            );
            if (otherLink) {
              otherLink.setAttribute("aria-expanded", "false");
            }
          }
        });

        // Toggle current dropdown
        item.setAttribute("aria-expanded", !isExpanded);
        link.setAttribute("aria-expanded", !isExpanded);
      };

      link.addEventListener("click", handleClick);

      // Handle hover events (desktop) - update aria-expanded for accessibility
      const handleMouseEnter = () => {
        const isMobile = window.matchMedia("(max-width: 1199px)").matches;
        if (isMobile) return;

        item.setAttribute("aria-expanded", "true");
        link.setAttribute("aria-expanded", "true");
      };

      const handleMouseLeave = () => {
        const isMobile = window.matchMedia("(max-width: 1199px)").matches;
        if (isMobile) return;

        item.setAttribute("aria-expanded", "false");
        link.setAttribute("aria-expanded", "false");
      };

      item.addEventListener("mouseenter", handleMouseEnter);
      item.addEventListener("mouseleave", handleMouseLeave);

      // Handle keyboard navigation
      const handleKeydown = (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          const isExpanded = item.getAttribute("aria-expanded") === "true";
          item.setAttribute("aria-expanded", !isExpanded);
          link.setAttribute("aria-expanded", !isExpanded);
        } else if (e.key === "Escape") {
          item.setAttribute("aria-expanded", "false");
          link.setAttribute("aria-expanded", "false");
          link.focus();
        }
      };

      link.addEventListener("keydown", handleKeydown);
    });
  }

  init() {
    if (this.isSticky) {
      this.enableStickyHeader();
    } else {
      this.disableStickyHeader();
    }

    // Initialize dropdown functionality
    // Use a small delay to ensure DOM is ready
    setTimeout(() => {
      this.initDropdowns();
    }, 100);

  }
}

customElements.define("page-header", PageHeaderSection);
